import { useState } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import Tip from '../ui/BettingTooltip';
import { renderInlineMarkdown } from '../../utils/renderMarkdown';

const TYPE_LABELS = { spread_value:'Spread', moneyline_value:'ML', total_value:'Total', steam_move:'Steam', reverse_line_movement:'RLM' };
const TYPE_TIPS = { spread_value:'spread', moneyline_value:'moneyline', total_value:'overunder', steam_move:'steam', reverse_line_movement:'rlm' };

const TYPE_STYLES = {
  spread_value:       { badge: 'bg-[#E8F0FE] text-[#005BB3] dark:bg-[rgba(0,91,179,0.15)] dark:text-[#3b82f6]', border: 'border-l-[#005BB3]' },
  moneyline_value:    { badge: 'bg-[#E6F5EC] text-[#029456] dark:bg-[rgba(2,148,86,0.15)] dark:text-[#34d399]', border: 'border-l-[#029456]' },
  total_value:        { badge: 'bg-[#FFF3EC] text-[#F86A38] dark:bg-[rgba(248,106,56,0.15)] dark:text-[#fb923c]', border: 'border-l-[#F86A38]' },
  steam_move:         { badge: 'bg-[#FEF0F0] text-[#DC2626] dark:bg-[rgba(220,38,38,0.15)] dark:text-[#ef4444]', border: 'border-l-[#DC2626]' },
  reverse_line_movement: { badge: 'bg-[#F8F9FA] text-[#4B5563] dark:bg-[rgba(75,85,99,0.15)] dark:text-[#9CA3AF]', border: 'border-l-[#4B5563]' },
};

const DEFAULT_STYLE = { badge: 'bg-[#F8F9FA] text-[#6B7280] dark:bg-[#1a2030] dark:text-[#8892a8]', border: 'border-l-[#E5E7EB]' };

export default function AlertCard({ alert }) {
  const [aiExplanation, setAiExplanation] = useState(alert.ai_explanation || null);
  const [loadingAi, setLoadingAi] = useState(false);
  const d = alert.discrepancy_data || {};
  const sport = alert.sport;
  const sportLabel = sport?.includes('nfl')?'NFL':sport?.includes('nba')?'NBA':sport?.includes('mlb')?'MLB':sport?.includes('mma')?'MMA':sport?.includes('boxing')?'Boxing':sport?.includes('golf')?'Golf':sport;
  const typeLabel = TYPE_LABELS[alert.alert_type] || alert.alert_type;
  const isSteam = alert.alert_type === 'steam_move';
  const isRlm = alert.alert_type === 'reverse_line_movement';
  const typeStyle = TYPE_STYLES[alert.alert_type] || DEFAULT_STYLE;

  const createdAt = alert.created_at ? new Date(alert.created_at).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}) : '';

  function fetchAi() {
    if (!alert.id) return;
    setLoadingAi(true);
    client.get(`/api/ai/value-explanation/${alert.id}`)
      .then((r) => setAiExplanation(r.data?.content))
      .catch(() => setAiExplanation('Unable to generate.'))
      .finally(() => setLoadingAi(false));
  }

  let desc;
  if (isRlm) {
    const o = d.old_value > 0 ? `+${d.old_value}` : d.old_value;
    const n = d.new_value > 0 ? `+${d.new_value}` : d.new_value;
    desc = `Public on ${d.public_side}, line moved ${o}\u2192${n} toward ${d.sharp_side} (${d.bookmaker_title})`;
  } else if (isSteam) {
    const bks = (d.bookmakers_moved||[]).join(', ');
    desc = `${d.count} books moved ${d.outcome_name} ${d.market==='h2h'?'ML':d.market} simultaneously (${bks})`;
  } else if (alert.alert_type === 'moneyline_value') {
    const p = d.bookmaker_price > 0 ? `+${d.bookmaker_price}` : d.bookmaker_price;
    const bip = d.bookmaker_implied_prob != null ? `${(d.bookmaker_implied_prob*100).toFixed(1)}%` : '—';
    const cip = d.consensus_implied_prob != null ? `${(d.consensus_implied_prob*100).toFixed(1)}%` : '—';
    desc = `${d.bookmaker_title}: ${d.outcome_name} at ${p} (${bip}) vs consensus ${cip}`;
  } else {
    const pt = d.bookmaker_point > 0 ? `+${d.bookmaker_point}` : d.bookmaker_point;
    const c = d.consensus_point > 0 ? `+${d.consensus_point}` : d.consensus_point;
    desc = `${d.bookmaker_title}: ${d.outcome_name} ${pt} vs consensus ${c}`;
  }

  return (
    <div className={`rounded-lg border border-[#E5E7EB] dark:border-[#1e2536] border-l-[3px] ${typeStyle.border} bg-white dark:bg-[#151a25] px-4 py-3 transition-colors duration-150`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${typeStyle.badge}`}>{isSteam && '\u26A1'}{isRlm && '\u21C4'} <Tip term={TYPE_TIPS[alert.alert_type]}>{typeLabel}</Tip></span>
          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[#F8F9FA] dark:bg-[#1a2030] text-[#6B7280] dark:text-[#8892a8]">{sportLabel}</span>
        </div>
        <span className="text-[13px] text-[#9CA3AF] whitespace-nowrap" style={{ fontFamily: 'var(--font-sans)' }}>{createdAt}</span>
      </div>
      <Link to={`/game/${alert.event_id}?sport=${sport}`} className="text-[14px] font-semibold text-[#111827] dark:text-[#e2e8f0] hover:text-[#005BB3] dark:hover:text-[#3b82f6] transition-colors duration-150" style={{ fontFamily: 'var(--font-sans)' }}>{d.away_team} @ {d.home_team}</Link>
      <p className="text-[13px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{desc}</p>
      {aiExplanation ? (
        <div className="mt-2 pt-2 border-t border-[#E5E7EB] dark:border-[#1e2536]">
          <p className="text-[11px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8] mb-0.5" style={{ fontFamily: 'var(--font-sans)' }}>AI</p>
          <p className="text-[13px] text-[#1F2937] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{renderInlineMarkdown(aiExplanation)}</p>
        </div>
      ) : (
        <button onClick={fetchAi} disabled={loadingAi} className="mt-1.5 text-[13px] text-[#005BB3] dark:text-[#3b82f6] hover:text-[#004494] transition-colors duration-150 disabled:opacity-40" style={{ fontFamily: 'var(--font-sans)' }}>
          {loadingAi ? 'Analyzing...' : 'Get AI Explanation'}
        </button>
      )}
    </div>
  );
}
